import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PeopleIcon from '@mui/icons-material/People';
import RefreshIcon from '@mui/icons-material/Refresh';
import { healthService } from '../Services/HealthService';
import {
  TenantHealthStatus,
  Props,
  TenantHealth,
  FarmHealth,
} from '../Model/HealthModels';
import TenantInsights from './TenantInsights';
import { StatusIcon } from '../Common/CommonMethods';
import { Cancel } from '@mui/icons-material';
import { farmService } from '../../Farm/FarmService';
import { customerService } from '../../Customer/CustomerService';
import CustomButton from '../../../../Common/Components/Controls/InputControls';

type TenantType = 'collaboration' | 'normal';

const tenantTypeConfig: Record<
  TenantType,
  { label: string; color: 'primary' | 'secondary' | 'info' | 'default' }
> = {
  collaboration: { label: 'Collaboration', color: 'info' },
  normal: { label: 'Standard', color: 'default' },
};

const getTenantType = (
  tenantId: string,
  collaborationIds: Set<string> | undefined,
): TenantType => {
  if (collaborationIds?.has(tenantId)) return 'collaboration';
  return 'normal';
};

const TenantHealthPage = ({ product }: Props) => {
  const [tenants, setTenants] = useState<TenantHealthStatus[]>([]);
  const [farms, setFarms] = useState<FarmHealth[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [collaborationIds, setCollaborationIds] = useState<Set<string>>(
    new Set(),
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadCollaborationTenants();
    loadActiveTenantsAndTenantHealth();
  }, [product]);

  const loadCollaborationTenants = async () => {
    try {
      const { Items } = await customerService.getAllCustomerModules();
      const collabIds = new Set<string>();
      Items.forEach((m) => {
        if (m.ModuleID === 'collaboration') collabIds.add(m.CustomerID);
      });
      setCollaborationIds(collabIds);
    } catch (error) {
      console.error('Error fetching collaboration tenants:', error);
    }
  };

  const loadActiveTenantsAndTenantHealth = async () => {
    try {
      const farmsData = await farmService.getFarmsByProduct(product);
      setFarms([]);
      farmsData.Items.forEach(async (farm, index) => {
        setFarms((prevFarms) => [
          ...prevFarms,
          {
            FarmName: farm.DisplayName,
            FarmNumber: index + 1,
            Id: farm.Id,
            TenantsHealth: [],
            loading: true,
          },
        ]);

        try {
          const tenants = await farmService.getMoniteredTenantsOnFarm(farm.Id);
          const tenantsHealth = await Promise.all(
            tenants.map((tenant) =>
              healthService.GetTenantHealthStatus(tenant.Id),
            ),
          );

          setFarms((prevFarms) =>
            prevFarms.map((f) =>
              f.Id === farm.Id
                ? { ...f, TenantsHealth: tenantsHealth, loading: false }
                : f,
            ),
          );
        } catch (error) {
          console.error(`Error loading tenants for farm ${farm.Id}`, error);
          setFarms((prevFarms) =>
            prevFarms.map((f) =>
              f.Id === farm.Id ? { ...f, loading: false } : f,
            ),
          );
        }
      });
    } catch (error) {
      console.error('Error loading farms:', error);
    }
  };

  const handleRowSelection = async (tenant: TenantHealth) => {
    setShowDialog(true);
    const data = await healthService.GetTenantHealth(tenant.Id);
    setTenants(data.Components);
  };

  const handleClose = () => {
    setTenants([]);
    setShowDialog(false);
  };

  return (
    <Paper elevation={1} sx={{ padding: 3, borderRadius: 3, mb: 4 }}>
      <Box display="flex" alignItems="center" gap={1.5} mb={4}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MonitorHeartIcon color="primary" fontSize="small" />
        </Box>
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          fontWeight={700}
          sx={{ letterSpacing: '-0.02em' }}
        >
          Tenant Health
        </Typography>
        <Box
          display="flex"
          gap={1}
          ml="auto"
          flexWrap="wrap"
          alignItems="center"
        >
          {(['collaboration', 'normal'] as TenantType[]).map((type) => (
            <Chip
              key={type}
              label={tenantTypeConfig[type].label}
              size="small"
              color={tenantTypeConfig[type].color}
              variant="outlined"
              sx={{ fontWeight: 600, px: 0.5 }}
            />
          ))}
          <IconButton
            size="small"
            onClick={loadActiveTenantsAndTenantHealth}
            sx={{
              bgcolor: 'action.hover',
              borderRadius: 2,
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {farms.map((farmHealth) => (
        <Paper
          key={farmHealth.Id}
          elevation={0}
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 3,
            bgcolor: 'background.default',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6" fontWeight={600}>
              {`Farm ${farmHealth.Id}: ${farmHealth.FarmName}`}
            </Typography>
            {!farmHealth.loading && (
              <Chip
                icon={<PeopleIcon fontSize="small" />}
                label={`${farmHealth.TenantsHealth.length} Active`}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ fontWeight: 600, bgcolor: 'background.paper' }}
              />
            )}
          </Box>

          {farmHealth.loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <LinearProgress
                sx={{
                  width: '100%',
                  borderRadius: 3,
                  height: 6,
                  bgcolor: 'action.selected',
                }}
              />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {farmHealth.TenantsHealth.map((tenant) => {
                const tenantType = getTenantType(tenant.Id, collaborationIds);
                const typeStyle = tenantTypeConfig[tenantType];
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={tenant.Id}>
                    <Tooltip title={typeStyle.label} placement="top" arrow>
                      <Card
                        elevation={0}
                        sx={{
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          border: `1px solid ${theme.palette.divider}`,
                          transition:
                            'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3,
                          },
                        }}
                      >
                        <CardContent sx={{ p: '16px !important' }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor:
                                    tenantType === 'collaboration'
                                      ? 'info.main'
                                      : 'text.disabled',
                                }}
                              />
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                {tenant.Name}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ cursor: 'pointer', display: 'flex' }}
                              onClick={() => handleRowSelection(tenant)}
                            >
                              {StatusIcon(
                                tenant.Status,
                                `${tenant.ResponseTime}s`,
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Tooltip>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      ))}

      <Dialog
        open={showDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, p: 3 }}>Tenant Sites</DialogTitle>
        <Divider />
        {tenants.length > 0 ? (
          <DialogContent sx={{ p: 0 }}>
            <TenantInsights Components={tenants} />
          </DialogContent>
        ) : (
          <Box display="flex" justifyContent="center" py={6} width="100%">
            <LinearProgress sx={{ width: '80%', borderRadius: 3 }} />
          </Box>
        )}
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <CustomButton
            onClick={handleClose}
            startIcon={<Cancel />}
            label="Close"
          />
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TenantHealthPage;
